import { Link } from 'react-router-dom'
import { useAuthStore } from '../../../store/authStore'
import type { OnboardingScenarioProps } from '../model/types'
import { useOnboardingFlow } from '../model/useOnboardingFlow'
import { ONBOARDING_TAGS } from '../model/onboarding.data'
import OnboardingBrand from './OnboardingBrand'
import styles from './Onboarding.module.css'

export default function HumanteqOnboarding(props: OnboardingScenarioProps) {
  const { submitting, error } = props
  const accessToken = useAuthStore((s) => s.accessToken)
  const { step, name, tags, styleKind, styleLabels, styleStepAvailable, action, handleNameChange, toggleTag, changeStyle } = useOnboardingFlow(props)

  // Per-step header + body; the action descriptor (label/handler/disabled)
  // comes from the flow hook. The approved mock is a 2-screen funnel: step 1
  // name + style chips, step 2 topics.
  let header = null
  let body = null

  if (step === 1) {
    header = (
      <header className={styles.header}>
        <h1 className={styles.h1}>Давайте познакомимся</h1>
        <p className={styles.lead}>Это поможет Хьюмантек обращаться к вам естественно.</p>
      </header>
    )
    body = (
      <>
        <label className={styles.fieldLabel} htmlFor="onboarding-name">Как вас зовут?</label>
        <input
          id="onboarding-name"
          className={styles.input}
          type="text"
          placeholder="Введите имя"
          autoComplete="nickname"
          value={name}
          onChange={handleNameChange}
        />
        {styleStepAvailable && (
          <>
            <p className={styles.fieldLabel}>Как вам сейчас лучше отвечать?</p>
            <p className={styles.lead}>Выберите стиль общения. Его можно будет поменять в любое время.</p>
            <div className={styles.chips}>
              {(['soft', 'analytic', 'challenge'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  className={`${styles.chip} ${styleKind === kind ? styles.chipSelected : ''}`}
                  data-kind={kind}
                  aria-pressed={styleKind === kind}
                  onClick={changeStyle}
                >
                  {styleLabels[kind]?.label ?? kind}
                </button>
              ))}
            </div>
          </>
        )}
      </>
    )
  } else {
    header = (
      <header className={styles.header}>
        <h1 className={styles.h1}>С чем хотите разобраться?</h1>
        <p className={styles.lead}>Выберите, что важно сейчас.</p>
      </header>
    )
    body = (
      <div className={styles.chips}>
        {ONBOARDING_TAGS.map((tag) => (
          <button
            key={tag}
            type="button"
            className={`${styles.chip} ${tags.includes(tag) ? styles.chipSelected : ''}`}
            data-tag={tag}
            aria-pressed={tags.includes(tag)}
            onClick={toggleTag}
          >
            {tag}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.screen}>
      <div className={styles.content}>
        <OnboardingBrand step={step} />

        {header}
        {/* Each step starts with a fresh scroll container on compact screens. */}
        <div key={step} className={styles.bodyArea}>{body}</div>

        <div className={styles.actions}>
          {error && <p role="alert">{error}</p>}
          <button
            className={styles.primaryBtn}
            onClick={action.onClick}
            disabled={action.disabled || submitting}
          >
            {submitting ? 'Настраиваем бота...' : action.label}
          </button>
          {step === 1 && !accessToken && (
            <p className={styles.loginLink}>
              Уже есть аккаунт? <Link to="/login">Войти</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
